import React, { FC, useEffect, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Stack from '@mui/material/Stack';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import TwCheckbox from '../TwCheckboxes/TwCheckboxes';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';

import './TwColumnsMenu.scss';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import {
  ActiveColumns,
  DEFAULT_ORDER_COLUMNS,
  DEFAULT_TRIP_COLUMNS,
  DEFAULT_AVAILABILITY_COLUMNS,
  DEFAULT_INVOICES_COLUMNS,
  DEFAULT_FUEL_TRANSACTION_COLUMNS,
  DEFAULT_CARRIER_COLUMNS
} from '../../providers/Settings/DisplaySettings.model';
import { Box } from '@mui/material';

interface TwColumnsMenuProps {
  activeColumns: ActiveColumns[];
  entity: 'order' | 'trip' | 'availability' | 'fuel transaction' | 'invoice' | 'carrier';
  onSaveColumnChanges: (activeColumns: ActiveColumns[]) => void;
  onReset: () => void;
  sx?: React.ComponentProps<typeof Box>['sx'];
}

const TwColumnsMenu: FC<TwColumnsMenuProps> = ({
  activeColumns,
  entity,
  onSaveColumnChanges,
  onReset,
  sx
}): JSX.Element => {
  const { addNotification } = useNotifications();
  const [columns, setColumns] = useState<ActiveColumns[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [enabled, setEnabled] = useState(false);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
  };

  const handleClose = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(null);
    event?.stopPropagation();
  };

  const onDragEnd = (result: DropResult): void => {
    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    setSaveButtonDisabled(false);
    const draggedItem = columns.find((column) => column.name === draggableId);
    if (draggedItem) {
      const newCols = Array.from(columns);
      newCols.splice(source.index, 1);
      newCols.splice(destination.index, 0, draggedItem);

      const reordered = newCols.map((col, i) => {
        return {
          ...col,
          index: i
        };
      });

      setColumns(reordered);
    }
  };

  const reset = (): void => {
    let defaultColumns;
    switch (entity) {
      case 'order':
        defaultColumns = DEFAULT_ORDER_COLUMNS;
        break;
      case 'trip':
        defaultColumns = DEFAULT_TRIP_COLUMNS;
        break;
      case 'availability':
        defaultColumns = DEFAULT_AVAILABILITY_COLUMNS;
        break;
      case 'fuel transaction':
        defaultColumns = DEFAULT_FUEL_TRANSACTION_COLUMNS;
        break;
      case 'invoice':
        defaultColumns = DEFAULT_INVOICES_COLUMNS;
        break;
      case 'carrier':
        defaultColumns = DEFAULT_CARRIER_COLUMNS;
        break;
      default:
        defaultColumns = DEFAULT_ORDER_COLUMNS;
    }
    setColumns(defaultColumns);
    onReset();
    setAnchorEl(null);
  };

  const onCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSaveButtonDisabled(false);
    const { name, checked } = event.target;
    const updatedColumns = columns.map((column) => {
      if (column.name === name) {
        return { ...column, visible: checked };
      }
      return column;
    });
    const atLeastOneColumnVisible = updatedColumns.some((column) => column.visible);
    if (atLeastOneColumnVisible) {
      setColumns(updatedColumns);
    } else {
      addNotification({
        message: 'At least one column must be visible',
        variant: 'error'
      });
    }
  };

  const save = (): void => {
    onSaveColumnChanges(columns);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (open) {
      const animation = requestAnimationFrame(() => setEnabled(true));
      return (): void => {
        cancelAnimationFrame(animation);
        setEnabled(false);
      };
    }
  }, [open]);

  useEffect(() => {
    if (open && activeColumns.length > 0) {
      setColumns(activeColumns);
    }
    if (!open) {
      setSaveButtonDisabled(true);
    }
  }, [open, activeColumns]);

  return (
    <Box sx={sx}>
      <IconButton
        disableRipple={true}
        onClick={handleClick}
        className="all-loads-customize-menu__btn"
      >
        <MoreVertIcon className="tw-load-menu__menuIcon" />
      </IconButton>

      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        {/* Header sticky */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 2,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid #eee'
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <span className="all-loads-customize-menu__title">Customize columns</span>
            <TwButton type={TwButtonType.text} label="Reset" action={reset} />
          </Stack>
        </Box>

        {/* Scrollable content */}
        <Box
          sx={{
            height: '65vh',
            overflowY: 'auto',
            flex: 1,
            px: 2,
            py: 1
          }}
        >
          <DragDropContext onDragEnd={onDragEnd}>
            {enabled && open && (
              <Droppable droppableId="customizable-columns">
                {(droppableProvided): JSX.Element => (
                  <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                    {columns.map((column: ActiveColumns, index) => (
                      <TwColumnsItems
                        onCheckboxChange={onCheckboxChange}
                        key={column.name}
                        item={column}
                        index={index}
                        isDraggable={true}
                      />
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
          </DragDropContext>
        </Box>

        {/* Footer sticky with save button */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'white',
            zIndex: 2,
            px: 2,
            py: 1.5,
            borderTop: '1px solid #eee'
          }}
        >
          <TwButton
            label="Save changes"
            action={save}
            type={TwButtonType.primary}
            fullwidth
            disabled={saveButtonDisabled}
          />
        </Box>
      </Menu>
    </Box>
  );
};

export default TwColumnsMenu;

const TwColumnsItems: FC<{
  item: ActiveColumns;
  index: number;
  onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isDraggable: boolean;
}> = ({ item, index, onCheckboxChange, isDraggable }): JSX.Element => {
  const content = (
    <div className="all-loads-customize-menu__item">
      <TwCheckbox
        name={item.name}
        label={item.label || item.name.charAt(0).toUpperCase() + item.name.slice(1)}
        checked={item.visible}
        onInputChange={onCheckboxChange}
      />
      {isDraggable && (
        <span className="all-loads-customize-menu__drag">
          <DragIndicatorIcon />
        </span>
      )}
    </div>
  );

  if (!isDraggable) {
    return content;
  }

  return (
    <Draggable draggableId={item.name} index={index}>
      {(draggableProvided): JSX.Element => (
        <div
          {...draggableProvided.draggableProps}
          ref={draggableProvided.innerRef}
          {...draggableProvided.dragHandleProps}
        >
          {content}
        </div>
      )}
    </Draggable>
  );
};
